/**
 * Contact Card — Table row for contact management with tonal layering.
 */

import { Pencil, Trash2, AlertCircle, CheckCircle2, Globe } from 'lucide-react';
import { PrimaryContactButton } from './PrimaryContactButton';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import { CONTACT_TYPE_NAMES, TYPE_ICONS, TYPE_COLORS } from '@/lib/contact-types';

import { type EditingContact } from './ContactSlideOver';

interface ContactCardProps {
  contact: {
    id: string;
    typeId: number;
    rank: number;
    contactValue: { val?: string; formattedAddress?: string };
    isActive: boolean;
    isRecovery: boolean;
    bounced: boolean;
    bounceCount: number;
  };
  onEdit: (contact: EditingContact) => void;
  onDelete: (id: string) => void;
  onSetPrimary: (id: string) => void;
}

export function ContactCard({ contact, onEdit, onDelete, onSetPrimary }: ContactCardProps) {
  const typeName = CONTACT_TYPE_NAMES[contact.typeId] || `Type ${contact.typeId}`;
  const value = contact.contactValue.val || contact.contactValue.formattedAddress || '';
  const icon = TYPE_ICONS[contact.typeId] || <Globe className="w-4 h-4" />;
  const colorClass = TYPE_COLORS[contact.typeId] || 'bg-surface-low text-muted-foreground';

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      onDelete(contact.id);
    }
  };

  return (
    <TableRow className="hover:bg-surface-low/50 transition-colors group">
      <TableCell className="py-5">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg ${colorClass} flex items-center justify-center`}>
            {icon}
          </div>
          <span className="text-sm font-semibold text-foreground">{typeName}</span>
        </div>
      </TableCell>
      <TableCell className={`font-mono text-xs ${contact.bounced ? 'text-muted-foreground line-through' : 'text-foreground/70'}`}>
        {value}
      </TableCell>
      <TableCell className="text-center">
        {contact.rank === 1 ? (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-[10px] font-black text-white">1</span>
        ) : (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-surface-low text-[10px] font-black text-muted-foreground">{contact.rank}</span>
        )}
      </TableCell>
      <TableCell>
        {contact.isActive ? (
          <Badge variant="success">Active</Badge>
        ) : contact.bounced ? (
          <Badge variant="destructive">Suspended</Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        )}
      </TableCell>
      <TableCell className="text-center">
        {contact.bounced ? (
          <AlertCircle className="w-5 h-5 text-destructive mx-auto" aria-label="Contact has bounced" />
        ) : (
          <CheckCircle2 className="w-5 h-5 text-surface-highest mx-auto" aria-label="No bounces" />
        )}
      </TableCell>
      <TableCell>
        {contact.isRecovery ? (
          <span className="text-sm text-primary font-medium">Recovery</span>
        ) : (
          <span className="text-sm text-muted-foreground italic">None</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(contact)}
            className="p-2 hover:bg-white hover:shadow-md rounded-lg text-muted-foreground hover:text-primary transition-all"
            aria-label={`Edit ${typeName} contact: ${value}`}
          >
            <Pencil className="w-4 h-4" aria-hidden="true" />
          </button>
          <PrimaryContactButton contactId={contact.id} rank={contact.rank} onSetPrimary={onSetPrimary} />
          <button
            onClick={handleDelete}
            className="p-2 hover:bg-white hover:shadow-md rounded-lg text-muted-foreground hover:text-destructive transition-all"
            aria-label={`Delete ${typeName} contact: ${value}`}
          >
            <Trash2 className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}
